import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useParams,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ShellLayout } from '@/components/ShellLayout';
import { MfeOutlet } from '@/mfe/MfeOutlet';
// AppsPage removed — /apps now loads the workspace MFE (parity with Angular shell)
import { SettingsPage } from '@/settings/SettingsPage';
import { LoginPage } from '@/auth/LoginPage';
import { FrameRedirectPage } from '@/auth/FrameRedirectPage';
import { Auth0Callback } from '@/auth/Auth0Callback';
import { ErrorPage } from '@/error/ErrorPage';
import {
  requireAuth,
  resolveTenant,
  validateRoute,
  validateScreenAccess,
  ensureAuth0ForMfe,
} from '@/auth/guards';
import { ensureAppRegistry, ensureAdminAppRegistry } from '@/apps/use-app-tiles';
import { canonicalAppId } from '@/apps/app-alias';
import { pushRouteState } from '@/router-state/route-state';
import { logPageView } from '@/telemetry/page-view';
import { ErrorBoundary } from '@/error/ErrorBoundary';
import { setRouterInstance, getRouterInstance } from '@/router/router-instance';
import { collapseClientScopedPath, clientAccessRedirect } from '@/router/client-navigation';
import { getTracker } from '@/telemetry/tracker';
import { pushToast } from '@/ui/toast-store';
import { useClientStore } from '@/client/client-store';
import { eventsModule } from '@/events/events-module';
import { LegacyPopupFallbackPage } from '@/router/LegacyPopupFallbackPage';
import { parseLegacyPopupOutletPath } from '@/router/legacy-route-adapter';
import { isPopupMfe } from '@/mfe/popup-mfe-constants';
import { openPopupMfe } from '@/mfe/popup-store';
import { useMfeMountStore } from '@/mfe/mfe-mount-store';

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Client identity is localStorage-only (legacy Angular parity — MFEs read
// `localStorage.currentlySelectedClientId`).
function readStoredClientId(): string | null {
  try {
    return localStorage.getItem('currentlySelectedClientId');
  } catch {
    return null;
  }
}

/**
 * Best-effort active clientId for building the canonical client-scoped URL:
 * the store's current client, else the last-selected id persisted to storage.
 * Returns null on a cold start (fresh login, clients not yet loaded) — callers
 * then fall back to a component that waits for the client list to resolve.
 */
function resolveClientId(): string | null {
  return useClientStore.getState().current?.id ?? readStoredClientId();
}

function persistLegacyRouteClient(clientId: string): void {
  if (!GUID_RE.test(clientId)) return;

  const { available, current, setCurrent } = useClientStore.getState();

  // If the store already has a client and it differs from the URL's client,
  // this is an MFE-initiated URL write. React MFEs (e.g. PIE) use their own
  // TanStack Router with basepath=/{clientId}/app-container/{appId}. When
  // they navigate internally, their router calls history.pushState/replaceState
  // with the full path including basepath. The shell's TanStack Router also
  // patches history and sees this as a route change, re-running beforeLoad.
  // If the MFE's basepath contains a stale clientId (from before a switch),
  // we must NOT flip the store — just ignore the stale URL. Do NOT rewrite
  // the URL here either, because that would trigger the MFE's router to
  // react again, creating a feedback loop between the two routers.
  if (current && current.id !== clientId) {
    return;
  }

  // Store has no client yet (cold start / deep link): persist the URL's
  // client to storage and store so the MFE can read it on mount.
  const previousId = current?.id ?? readStoredClientId();

  // Direct / hard-URL navigation into a DIFFERENT client than the one last used
  // (address bar, external deep link, same-tab paste). Record the pending change
  // so the shell can confirm before it takes effect — parity with the legacy
  // BaseRouteGuard "ALL OPEN TABS will be refreshed" dialog. A normal same-client
  // refresh (previousId === clientId) and a fresh login (no previous client) do
  // not prompt. Dropdown and Back/Forward switches never reach here: they keep an
  // in-memory current (handled by the early return above / ClientSwitchBackGuard).
  if (previousId && previousId !== clientId) {
    useClientStore.getState().setPendingDirectSwitch({ from: previousId, to: clientId });
  }

  const next = available.find((client) => client.id === clientId);
  if (next) {
    setCurrent(next, { source: 'url-change' });
    return;
  }

  // Cold start / deep link: the client list has not loaded yet, so there is no
  // full `ClientInfo` to commit via `setCurrent`. Seed the store's bootstrap
  // id together with legacy storage from a single writer, so imperative reads
  // (SDK `clientModule`, `EY_RAPToR_getCurrentClientId`, iframe URL builder)
  // resolve this client immediately instead of lagging localStorage until
  // `useClients` hydrates `current`.
  useClientStore.getState().setBootstrapClientId(clientId, previousId);
}

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => (
    <section style={{ padding: '2rem' }}>
      <h1>Page not found</h1>
      <p>
        The route <code>{typeof window !== 'undefined' ? window.location.pathname : ''}</code> is
        not registered in the shell.
      </p>
    </section>
  ),
});

/**
 * Pathless authenticated layer — all routes nested here require an MSAL
 * active account. Mirrors the legacy `MasterRouteGuard → BaseRouteGuard →
 * MsalGuard` chain.
 *
 * Sequential guard order (matches `MasterRouteGuard.getOrderedGuards`):
 *   1. `requireAuth`   — MSAL session check, kicks loginRedirect on miss.
 *   2. `resolveTenant` — clientId resolution from storage / URL.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  component: AuthenticatedShell,
  beforeLoad: async (ctx) => {
    await requireAuth({ location: ctx.location });
    resolveTenant({ location: ctx.location });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const frameRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/frameredirect',
  component: FrameRedirectPage,
});

/**
 * Auth0 redirect callback routes — the default redirect URI `/home` (Angular
 * POC parity) and the explicit `/auth0-callback`. Registered TOP-LEVEL (like
 * `frameRedirectRoute`, NOT under `authenticatedRoute`): at callback time the
 * Auth0 response is still being processed and `requireAuth` must not intercept.
 * The component itself no-ops on a plain visit (no `code`/`state`/`error`) and
 * forwards to the shell home.
 */
const auth0CallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth0-callback',
  component: Auth0Callback,
});

const auth0HomeCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  component: Auth0Callback,
});

const errorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/error',
  component: ErrorPage,
  beforeLoad: async (ctx) => {
    // Legacy guards /error with MsalGuard — only authenticated users reach it.
    await requireAuth({ location: ctx.location });
  },
});

const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  beforeLoad: () => {
    const clientId = resolveClientId();
    if (clientId) {
      throw redirect({ href: `/${clientId}/app-container/home`, replace: true });
    }
  },
  component: () => <ClientScopedWait appId="home" />,
});

const appsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/apps',
  beforeLoad: () => {
    const clientId = resolveClientId();
    if (clientId) {
      throw redirect({ href: `/${clientId}/app-container/workspace`, replace: true });
    }
  },
  component: () => <ClientScopedWait appId="workspace" />,
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: SettingsPage,
});

const legacyPopupContactRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/apps/$popupAppId/contact-support',
  component: LegacyPopupContactPage,
});

const legacyPopupRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/apps/$popupAppId',
  component: LegacyPopupPage,
});

/**
 * Catch-all MFE route. URL `/app/:appId/*` mounts the registered MFE matching
 * `:appId`. The trailing `*` is forwarded as `route.pathname` via MfeProps so
 * MFEs can do their own internal routing.
 *
 * `validateRoute` mirrors `ValidateRouteGuard` and runs *after* the
 * authenticated-layer guards (sequential per legacy MasterRouteGuard).
 */
const mfeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/app/$appId/$',
  beforeLoad: ({ params }) => {
    const splat = (params as { _splat?: string })._splat ?? '';
    const legacyPopup = parseLegacyPopupOutletPath(
      `/app/${params.appId}${splat ? `/${splat}` : ''}`,
    );
    if (legacyPopup) {
      throw redirect({
        href: `${legacyPopup.primaryPath}?popup=${encodeURIComponent(legacyPopup.popupAppId)}`,
        replace: true,
      });
    }
    validateRoute(params.appId);
    const clientId = resolveClientId();
    if (clientId) {
      const tail = splat ? `/${splat}` : '';
      // Keep the URL segment as-is (no MAD→pie rewrite): the alias is resolved
      // only for the loader + role check on the client-scoped mount route, so
      // the shell never fights the MFE over the path segment in history.
      throw redirect({
        href: `/${clientId}/app-container/${params.appId}${tail}`,
        replace: true,
      });
    }
  },
  component: MfeRedirectPage,
});

/**
 * Clientless admin deep-link / nav target produced by `toAppPath`
 * (`/app/admin/apps/{appId}`). Forwards to the canonical client-scoped admin
 * URL once the active client is known — the admin-path analogue of `mfeRoute`.
 * Its static `admin/apps` segments outrank `mfeRoute`'s `/app/$appId/$`.
 */
const adminAppRedirectRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/app/admin/apps/$appId/$',
  beforeLoad: ({ params }) => {
    validateRoute(params.appId);
    const clientId = resolveClientId();
    if (clientId) {
      const splat = (params as { _splat?: string })._splat ?? '';
      const tail = splat ? `/${splat}` : '';
      throw redirect({
        href: `/${clientId}/app-container/admin/apps/${params.appId}${tail}`,
        replace: true,
      });
    }
  },
  component: AdminMfeRedirectPage,
});

/**
 * Clientless workspace deep-link / nav target produced by `toAppPath`
 * (`/app/workspace/apps/{appId}`). Forwards to the canonical client-scoped
 * workspace URL once the active client is known — the workspace analogue of
 * `adminAppRedirectRoute`. Its static `workspace/apps` segments outrank
 * `mfeRoute`'s `/app/$appId/$`.
 */
const workspaceAppRedirectRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/app/workspace/apps/$appId/$',
  beforeLoad: ({ params }) => {
    validateRoute(params.appId);
    const clientId = resolveClientId();
    if (clientId) {
      const splat = (params as { _splat?: string })._splat ?? '';
      const tail = splat ? `/${splat}` : '';
      throw redirect({
        href: `/${clientId}/app-container/workspace/apps/${params.appId}${tail}`,
        replace: true,
      });
    }
  },
  component: WorkspaceMfeRedirectPage,
});

/**
 * Compatibility for legacy/deep-link URLs observed as `/{appId}/app`.
 * The React shell's canonical MFE host is `/app/{appId}`, but this keeps
 * existing app-first links from landing on the shell 404 before MfeOutlet
 * has a chance to mount the legacy custom element.
 */
const appFirstCompatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/$appId/app/$',
  beforeLoad: ({ params }) => {
    validateRoute(params.appId);
    const splat = (params as { _splat?: string })._splat ?? '';
    throw redirect({
      to: '/app/$appId/$',
      params: { appId: params.appId, _splat: splat },
      replace: true,
    });
  },
});

const appFirstCompatNoSplatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/$appId/app',
  beforeLoad: ({ params }) => {
    validateRoute(params.appId);
    throw redirect({
      to: '/app/$appId/$',
      params: { appId: params.appId, _splat: '' },
      replace: true,
    });
  },
});

/**
 * Clientless `/app-container/...` compatibility — parity with Angular's SECOND
 * app-routing entry `{ path: 'app-container', loadChildren: AppContainerModule }`
 * (no `:client-id`), which loads the same container module and lets
 * `BaseRouteGuard` resolve the client from storage and redirect to
 * `/{clientId}/app-container/...`. Without this a deep link that dropped its
 * client GUID (bookmark, external link, hand-typed URL) matched no React route
 * and fell through to the shell 404 ("route ... is not registered"). Resolve
 * the active client and forward, preserving the full sub-path + search so the
 * existing client-scoped routes then run their persistence + role checks. The
 * static `app-container` segment can only match a clientless URL: a
 * client-scoped `/{guid}/app-container/...` starts with the GUID segment and is
 * still claimed by the `/$clientId/app-container/...` routes.
 */
const clientlessAppContainerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/app-container/$',
  beforeLoad: ({ params, location }) => {
    const clientId = resolveClientId();
    if (clientId) {
      const splat = (params as { _splat?: string })._splat ?? '';
      const tail = splat ? `/${splat}` : '';
      throw redirect({
        href: `/${clientId}/app-container${tail}${location.searchStr}`,
        replace: true,
      });
    }
  },
  component: ClientlessAppContainerWait,
});

const clientlessAppContainerBareRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/app-container',
  beforeLoad: () => {
    const clientId = resolveClientId();
    if (clientId) {
      throw redirect({ href: `/${clientId}/app-container/home`, replace: true });
    }
  },
  component: () => <ClientScopedWait appId="home" />,
});

/**
 * Canonical client-scoped MFE route — byte-parity with the Angular shell URL
 * `/{clientId}/app-container/{appId}/...` (`raptor-shell-ui/src/app/
 * app-routing.module.ts`, where `:clientId` is the first path segment and
 * `app-container` mounts the dynamic MFE outlet). This is the REAL mount
 * (no longer a redirect to `/app/...`): keeping the clientId + `app-container`
 * in the path means the MFE `base-href` (`/{clientId}/app-container/{appId}`)
 * is again a prefix of the browser URL, so Angular's `location.path(true)`
 * strips it correctly (the mismatch that previously caused NG04002 is gone).
 * Old bookmarks / deep links resolve here directly.
 */
const legacyAppContainerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/$clientId/app-container/$appId/$',
  beforeLoad: async ({ params, location }) => {
    const legacyPopup = parseLegacyPopupOutletPath(location.pathname);
    if (legacyPopup) {
      throw redirect({
        href: `${legacyPopup.primaryPath}?popup=${encodeURIComponent(legacyPopup.popupAppId)}`,
        replace: true,
      });
    }
    // Bug #2: a buggy MFE relative-nav can append nested
    // `{clientId}/app-container/{appId}` groups, growing the URL and alternating
    // clients. Collapse to a single scope and replace history before mounting.
    const canonical = collapseClientScopedPath(location.pathname);
    if (canonical !== location.pathname) {
      throw redirect({ href: `${canonical}${location.searchStr}`, replace: true });
    }
    // MAD ("Master Assets and Data") is the product alias for the legacy `pie`
    // Data app. We DELIBERATELY do NOT rewrite the URL segment here. The Data
    // MFE is served under the `mad` segment for clients whose tile feed uses it
    // and drives its OWN router on that segment; rewriting `mad`→`pie` made the
    // shell and the MFE fight over the path in the shared history, producing an
    // endless `pie`⇄`mad` redirect loop. The alias is instead applied only where
    // it must match the deployed bundle + role-screen name: the loader (see
    // `ClientMfePage`) and the role check below. The browser URL keeps whatever
    // segment the MFE uses.
    persistLegacyRouteClient(params.clientId);
    // Bug #1: populate the app load-registry (appTypeID / externalRoutePath)
    // before the MFE mounts, so iframe apps resolve to the iframe loader instead
    // of defaulting to module-federation and 404-ing. Also warms the role-screen
    // mapping the guard below reuses (single fetch).
    await ensureAppRegistry(params.clientId);
    await validateScreenAccess(canonicalAppId(params.appId), params.clientId ?? null);
    await ensureAuth0ForMfe(params.appId, params.clientId ?? null);
  },
  component: ClientMfePage,
});

const legacyAppContainerNoSplatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/$clientId/app-container/$appId',
  beforeLoad: async ({ params }) => {
    // MAD → pie alias is applied only to the loader + role check, never the URL
    // segment (see `legacyAppContainerRoute` for why rewriting it loops).
    persistLegacyRouteClient(params.clientId);
    await ensureAppRegistry(params.clientId);
    await validateScreenAccess(canonicalAppId(params.appId), params.clientId ?? null);
    await ensureAuth0ForMfe(params.appId, params.clientId ?? null);
  },
  component: ClientMfePage,
});

/**
 * Canonical client-scoped ADMIN MFE routes — byte-parity with the legacy admin
 * URL `/{clientId}/app-container/admin/apps/{appId}/...` (raptor
 * `app-container-routing.module.ts`, pattern `admin/apps/:app-id`). The static
 * `admin/apps` segments outrank the generic `legacyAppContainerRoute`, so the
 * FINAL segment is the mounted appId and the `admin/apps/` prefix is retained as
 * admin context. Admin tiles are folded into the load registry (correct loader)
 * and the role check validates the FULL screen path, both mirroring legacy.
 */
const adminAppContainerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/$clientId/app-container/admin/apps/$appId/$',
  beforeLoad: async ({ params, location }) => {
    const legacyPopup = parseLegacyPopupOutletPath(location.pathname);
    if (legacyPopup) {
      throw redirect({
        href: `${legacyPopup.primaryPath}?popup=${encodeURIComponent(legacyPopup.popupAppId)}`,
        replace: true,
      });
    }
    const canonical = collapseClientScopedPath(location.pathname);
    if (canonical !== location.pathname) {
      throw redirect({ href: `${canonical}${location.searchStr}`, replace: true });
    }
    persistLegacyRouteClient(params.clientId);
    await ensureAppRegistry(params.clientId);
    await ensureAdminAppRegistry(params.clientId);
    await validateScreenAccess(params.appId, params.clientId ?? null, `admin/apps/${params.appId}`);
    await ensureAuth0ForMfe(params.appId, params.clientId ?? null);
  },
  component: AdminMfePage,
});

const adminAppContainerNoSplatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/$clientId/app-container/admin/apps/$appId',
  beforeLoad: async ({ params }) => {
    persistLegacyRouteClient(params.clientId);
    await ensureAppRegistry(params.clientId);
    await ensureAdminAppRegistry(params.clientId);
    await validateScreenAccess(params.appId, params.clientId ?? null, `admin/apps/${params.appId}`);
    await ensureAuth0ForMfe(params.appId, params.clientId ?? null);
  },
  component: AdminMfePage,
});

/**
 * Canonical client-scoped WORKSPACE MFE routes — byte-parity with the legacy
 * workspace URL `/{clientId}/app-container/workspace/apps/{appId}/...` (raptor
 * `app-container-routing.module.ts`, pattern `workspace/apps/:app-id`). The
 * static `workspace/apps` segments outrank the generic `legacyAppContainerRoute`
 * (which would otherwise mount the `workspace` launcher MFE with `apps/{appId}`
 * as splat), so the FINAL segment is the mounted appId and the
 * `workspace/apps/` prefix is retained in the URL + `base-href` exactly as the
 * deployed workspace MFEs expect (legacy `mfe-container.component.ts:262` sets
 * the base-href to the FULL route path). The role check validates the FULL
 * screen path `workspace/apps/{appId}`, mirroring legacy.
 */
const workspaceAppContainerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/$clientId/app-container/workspace/apps/$appId/$',
  beforeLoad: async ({ params, location }) => {
    const legacyPopup = parseLegacyPopupOutletPath(location.pathname);
    if (legacyPopup) {
      throw redirect({
        href: `${legacyPopup.primaryPath}?popup=${encodeURIComponent(legacyPopup.popupAppId)}`,
        replace: true,
      });
    }
    const canonical = collapseClientScopedPath(location.pathname);
    if (canonical !== location.pathname) {
      throw redirect({ href: `${canonical}${location.searchStr}`, replace: true });
    }
    persistLegacyRouteClient(params.clientId);
    await ensureAppRegistry(params.clientId);
    await validateScreenAccess(
      params.appId,
      params.clientId ?? null,
      `workspace/apps/${params.appId}`,
    );
    await ensureAuth0ForMfe(params.appId, params.clientId ?? null);
  },
  component: WorkspaceMfePage,
});

const workspaceAppContainerNoSplatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/$clientId/app-container/workspace/apps/$appId',
  beforeLoad: async ({ params }) => {
    persistLegacyRouteClient(params.clientId);
    await ensureAppRegistry(params.clientId);
    await validateScreenAccess(
      params.appId,
      params.clientId ?? null,
      `workspace/apps/${params.appId}`,
    );
    await ensureAuth0ForMfe(params.appId, params.clientId ?? null);
  },
  component: WorkspaceMfePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  frameRedirectRoute,
  auth0CallbackRoute,
  auth0HomeCallbackRoute,
  errorRoute,
  authenticatedRoute.addChildren([
    indexRoute,
    appsRoute,
    settingsRoute,
    legacyPopupContactRoute,
    legacyPopupRoute,
    adminAppRedirectRoute,
    workspaceAppRedirectRoute,
    mfeRoute,
    appFirstCompatRoute,
    appFirstCompatNoSplatRoute,
    clientlessAppContainerRoute,
    clientlessAppContainerBareRoute,
    adminAppContainerRoute,
    adminAppContainerNoSplatRoute,
    workspaceAppContainerRoute,
    workspaceAppContainerNoSplatRoute,
    legacyAppContainerRoute,
    legacyAppContainerNoSplatRoute,
  ]),
]);

const router = createRouter({ routeTree });

// Expose the router instance to non-React callers (legacy `routeToMFE` window
// globals, header bridge, MFE loader) so they can drive navigation through
// TanStack's own history instead of a raw `pushState` the router ignores.
setRouterInstance(router);

// Wire route-state tracker — every successful navigation feeds the
// `EY_RAPToR_pushRouteState` shim (M2.9/M2.10 parity).
router.subscribe('onResolved', () => {
  const loc = router.state.location;
  pushRouteState({ currentRoute: loc.pathname, fullRoute: loc.href });
  // Gated page-view logging (skip login/frameredirect, workspace→Apps) —
  // legacy `UILoggerService.logPageView` parity.
  logPageView(loc.pathname, loc.href);
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}

function AuthenticatedShell() {
  return (
    <ShellLayout>
      <ErrorBoundary context="shell_content">
        <Outlet />
      </ErrorBoundary>
    </ShellLayout>
  );
}

/**
 * Shared MFE host. Gates on client readiness, then mounts the registered MFE
 * for `appId` via {@link MfeOutlet}. Rendered by {@link ClientMfePage} for the
 * canonical `/{clientId}/app-container/{appId}` routes.
 *
 * Legacy MFEs gate template branches on the `client` attribute (set from
 * `clientId`). Without it `*ngIf="client"` evaluates false and the MFE
 * renders an empty shell. Mirrors `mfe-container.component.ts:171` in
 * raptor-shell-ui where `client` + `base-href` are written before mount.
 */
function MfeHost({
  appId,
  clientId: urlClientId,
  splat,
  basePath,
}: {
  appId: string;
  /**
   * ClientId from the URL path (route param). Single source of truth for the
   * MFE's client context so `baseHref` and `mfeProps.clientId` can never
   * diverge — the divergence (URL param vs store) fed the MFE contradictory
   * client context and drove the Bug #2 client-scoped redirect loop.
   */
  clientId: string;
  splat: string;
  /**
   * URL prefix the MFE is mounted under — MUST equal the actual
   * `window.location.pathname` prefix for this route (e.g. `/app/pie`).
   */
  basePath: string;
}) {
  const currentClient = useClientStore((s) => s.current);
  const availableClients = useClientStore((s) => s.available);

  // Portal refresh (legacy `triggerMFERefresh` → `EY_RAPToR_PortalRefresh`):
  // a SignalR portal/RUM update can ask the mounted MFE to reload. Bumping this
  // nonce changes the MfeOutlet `key` below, remounting (reloading) the MFE —
  // the React equivalent of the legacy in-place MFE refresh.
  const [refreshNonce, setRefreshNonce] = useState(0);
  useEffect(() => eventsModule.on('shell:portal-refresh', () => setRefreshNonce((n) => n + 1)), []);

  // Bug #7/#8: mark this app as loading whenever the host (re)mounts for a new
  // appId/client/refresh. The blocking overlay reads this to fence off the nav
  // rail, and the Home footer waits for `onLoaded` to clear it.
  const beginMount = useMfeMountStore((s) => s.begin);
  useEffect(() => {
    beginMount(appId);
  }, [appId, urlClientId, refreshNonce, beginMount]);

  // Client-access gate: once the RUM client list has loaded, a URL clientId that
  // is NOT one of the user's accessible clients means they have no access to
  // that client at all. Bounce them to their first available client's home and
  // warn (legacy parity). Returns null while the list is still loading or when
  // the client is accessible, so this never fires prematurely.
  const deniedClientHref = clientAccessRedirect(
    urlClientId,
    availableClients,
    currentClient?.id ?? null,
  );
  useEffect(() => {
    if (!deniedClientHref) return;
    getTracker().event('route_guard_blocked', {
      guard: 'requireClientAccess',
      reason: 'client_not_in_available',
      redirect_target: deniedClientHref,
    });
    pushToast({
      variant: 'error',
      message: "You don't have access to the requested URL. Redirecting to an available client.",
    });
    getRouterInstance()?.history.replace(deniedClientHref);
  }, [deniedClientHref]);

  if (!currentClient || availableClients.length === 0) {
    return <div data-mfe-pending-client={appId} style={{ minHeight: '100%' }} />;
  }

  // Access to the URL's client was denied — render a placeholder while the
  // effect above redirects to the fallback client's home (avoids mounting the
  // MFE for a client the user cannot access).
  if (deniedClientHref) {
    return <div data-mfe-client-denied={appId} style={{ minHeight: '100%' }} />;
  }

  // Single source of truth: the URL clientId. Its display details come from the
  // available list (fallback to the store's current client on a cold miss).
  const clientId = urlClientId || currentClient.id;
  const activeClient = availableClients.find((c) => c.id === clientId) ?? currentClient;
  // `base-href` MUST be a prefix of the real browser URL. Legacy MFEs use
  // Angular's PathLocationStrategy and bootstrap with
  // `router.navigateByUrl(location.path(true))` — `location.path(true)` returns
  // the current pathname with the base-href stripped. The shell now mounts MFEs
  // under the legacy client-scoped URL `/{clientId}/app-container/{appId}`
  // (see `legacyAppContainerRoute`), so `basePath` is exactly that prefix and
  // Angular strips it cleanly. The deep route is delivered via `routeParams`.
  const baseHref = basePath;
  const routeParams = splat
    ? splat
        .split('/')
        .filter(Boolean)
        .map((seg: string) => ({ value: seg, metadata: {} }))
    : [];
  return (
    <MfeOutlet
      // Loading UI is owned by the shell's full-viewport blocking overlay
      // (Bug #8), which also fences off the nav rail — so suppress the outlet's
      // own inline spinner to avoid a double loader. `onLoaded` clears the
      // shell mount state (fires on success AND error).
      suppressSpinner
      onLoaded={() => useMfeMountStore.getState().end(appId)}
      // Key intentionally excludes `splat`: once an MFE mounts it owns its own
      // internal routing and rewrites the URL splat as the user navigates
      // inside it (e.g. pie → `/app/pie/<id>/legal-entity/grid`). Including the
      // splat would change the key on every internal navigation and remount
      // (reload) the MFE in a loop. Only an appId change or explicit portal
      // refresh should remount via key. Client switches are handled by
      // MfeOutlet's clientId prop (in-place unload/load on the same container),
      // matching Angular shell behaviour and avoiding MF shared singleton
      // corruption from destroyPlatform().
      key={`${appId}:${refreshNonce}`}
      appId={appId}
      clientId={clientId}
      mfeProps={{
        clientId,
        client: {
          // Always the unified `clientId` (never the fallback client's id) so
          // the MFE's client context stays internally consistent with baseHref.
          clientId,
          clientName: activeClient.name,
          isFavorite: activeClient.isFavorite,
        },
        baseHref,
        routeParams,
        route: {
          pathname: `/${splat}`,
          params: {},
          search: typeof window !== 'undefined' ? window.location.search : '',
        },
      }}
    />
  );
}

/**
 * Canonical client-scoped MFE host. Mounts the MFE for `appId` under its legacy
 * `base-href` `/{clientId}/app-container/{appId}` (a real prefix of the browser
 * URL). Rendered by the `/{clientId}/app-container/{appId}[/{splat}]` routes;
 * `home` and `workspace` are ordinary appIds under this scheme.
 */
function ClientMfePage() {
  const params = useParams({ strict: false }) as {
    clientId?: string;
    appId?: string;
    _splat?: string;
  };
  const clientId = params.clientId ?? '';
  const urlAppId = params.appId ?? '';
  // Load + mount the MFE for the RAW URL segment (e.g. `mad` → the deployed
  // `/static/mad/` bundle), NOT the canonical alias. `mad` ("Master Assets and
  // Data") is the Next.js successor to the old Angular `pie` Data app and ships
  // as its OWN MFE. The deployed `pie` bundle, when its `isNextJsEnabled` flag
  // is on, calls `window.location.assign(.../app-container/mad?projectGuid=…)`
  // to hand off to it. Aliasing `mad`→`pie` HERE loaded the OLD `pie` bundle on
  // the `mad` URL, which then re-assigned to `mad` on every boot → an infinite
  // full-page reload loop. The `mad`→`pie` alias is applied ONLY to the
  // role-screen check (`validateScreenAccess` in the route `beforeLoad`), where
  // the Next.js app reuses the `pie` mapping. `baseHref` already uses the raw
  // `urlAppId`, so the MFE drives its own router on the segment it owns.
  const loadAppId = urlAppId.toLowerCase();
  return (
    <MfeHost
      appId={loadAppId}
      clientId={clientId}
      splat={params._splat ?? ''}
      basePath={`/${clientId}/app-container/${urlAppId}`}
    />
  );
}

/**
 * Client-scoped ADMIN MFE host. Mounts the MFE for the FINAL segment (`appId`)
 * of `/{clientId}/app-container/admin/apps/{appId}` under the full legacy admin
 * base-href (a real prefix of the browser URL, so Angular's PathLocationStrategy
 * strips it cleanly). Rendered by the admin `app-container/admin/apps/...` routes.
 */
function AdminMfePage() {
  const params = useParams({ strict: false }) as {
    clientId?: string;
    appId?: string;
    _splat?: string;
  };
  const clientId = params.clientId ?? '';
  const appId = params.appId ?? '';
  return (
    <MfeHost
      appId={appId}
      clientId={clientId}
      splat={params._splat ?? ''}
      basePath={`/${clientId}/app-container/admin/apps/${appId}`}
    />
  );
}

/**
 * Client-scoped WORKSPACE MFE host. Mounts the MFE for the FINAL segment
 * (`appId`) of `/{clientId}/app-container/workspace/apps/{appId}` under the full
 * legacy workspace base-href (a real prefix of the browser URL, so Angular's
 * PathLocationStrategy strips it cleanly). Rendered by the workspace
 * `app-container/workspace/apps/...` routes.
 */
function WorkspaceMfePage() {
  const params = useParams({ strict: false }) as {
    clientId?: string;
    appId?: string;
    _splat?: string;
  };
  const clientId = params.clientId ?? '';
  const appId = params.appId ?? '';
  return (
    <MfeHost
      appId={appId}
      clientId={clientId}
      splat={params._splat ?? ''}
      basePath={`/${clientId}/app-container/workspace/apps/${appId}`}
    />
  );
}

/**
 * Cold-start bridge for the legacy React-era / clientless URLs (`/`, `/apps`,
 * `/app/{appId}`). When the active client isn't known yet (fresh login, client
 * list still loading) these routes render this component instead of redirecting
 * in `beforeLoad`; once the client store resolves it forwards to the canonical
 * `/{clientId}/app-container/{appId}` URL (replacing history so Back skips it).
 */
function ClientScopedWait({
  appId,
  splat = '',
  popup,
  scope,
}: {
  appId: string;
  splat?: string;
  popup?: string;
  /** Legacy mount scope prefix — `admin/apps` or `workspace/apps` — or none. */
  scope?: 'admin/apps' | 'workspace/apps';
}) {
  const current = useClientStore((s) => s.current);
  useEffect(() => {
    if (!current) return;
    const tail = splat ? `/${splat}` : '';
    const search = popup ? `?popup=${encodeURIComponent(popup)}` : '';
    const container = scope ? `${scope}/${appId}` : appId;
    getRouterInstance()?.history.replace(
      `/${current.id}/app-container/${container}${tail}${search}`,
    );
  }, [current, appId, splat, popup, scope]);
  return <div data-mfe-pending-client={appId} style={{ minHeight: '100%' }} />;
}

function MfeRedirectPage() {
  const { appId, _splat } = mfeRoute.useParams();
  return <ClientScopedWait appId={appId} splat={_splat ?? ''} />;
}

/**
 * Cold-start bridge for a clientless `/app-container/{path}` deep link when the
 * active client isn't resolved yet (fresh login, client list still loading).
 * Once the store resolves it forwards to `/{clientId}/app-container/{path}`,
 * preserving the requested sub-path + search so the deep link isn't lost.
 */
function ClientlessAppContainerWait() {
  const { _splat } = clientlessAppContainerRoute.useParams();
  const current = useClientStore((s) => s.current);
  useEffect(() => {
    if (!current) return;
    const tail = _splat ? `/${_splat}` : '';
    const search = typeof window !== 'undefined' ? window.location.search : '';
    getRouterInstance()?.history.replace(`/${current.id}/app-container${tail}${search}`);
  }, [current, _splat]);
  return <div data-mfe-pending-client="app-container" style={{ minHeight: '100%' }} />;
}

function AdminMfeRedirectPage() {
  const params = useParams({ strict: false }) as { appId?: string; _splat?: string };
  return (
    <ClientScopedWait appId={params.appId ?? ''} splat={params._splat ?? ''} scope="admin/apps" />
  );
}

function WorkspaceMfeRedirectPage() {
  const params = useParams({ strict: false }) as { appId?: string; _splat?: string };
  return (
    <ClientScopedWait
      appId={params.appId ?? ''}
      splat={params._splat ?? ''}
      scope="workspace/apps"
    />
  );
}

/**
 * Deep-link / refresh host for popup MFEs. A URL like `/apps/stf` opens the
 * popup overlay over the client's home MFE — legacy parity for "popup MFEs are
 * directly accessible by URL". Known popups forward to
 * `/{clientId}/app-container/home?popup={appId}`; unknown ids fall back to the
 * info page.
 */
function PopupRouteHost({
  appId,
  target,
  subPath,
}: {
  appId: string;
  target: string;
  subPath?: string;
}) {
  const isPopup = isPopupMfe(appId);
  useEffect(() => {
    if (isPopup) openPopupMfe(appId, subPath);
  }, [appId, isPopup, subPath]);
  return isPopup ? (
    <ClientScopedWait appId="home" popup={appId} />
  ) : (
    <LegacyPopupFallbackPage target={target} />
  );
}

function LegacyPopupPage() {
  const { popupAppId } = legacyPopupRoute.useParams();
  return <PopupRouteHost appId={popupAppId} target={`apps/${popupAppId}`} />;
}

function LegacyPopupContactPage() {
  const { popupAppId } = legacyPopupContactRoute.useParams();
  return (
    <PopupRouteHost
      appId={popupAppId}
      target={`apps/${popupAppId}/contact-support`}
      subPath="contact-support"
    />
  );
}
